import { kSandboxSignalName } from "../transform/fixups";
import { isSentinelSpan } from "../transform/toolSentinels";
import { TYPE_SCORER, TYPE_SCORERS } from "../transform/utils";
import { EventNode, eventNodeOf } from "../types";

const kTurnType = "turn";
const kTurnsType = "turns";
const kCollapsedScoring = "scorings";

// Visitors are used to transform the event tree
export const removeNodeVisitor = (event: string) => {
  return {
    visit: (node: EventNode): EventNode[] => {
      if (node.event.event === event) {
        return [];
      }
      return [node];
    },
  };
};

/** Drops a sentinel's span with its contents; the monitor model calls in it are not agent turns. */
export const removeSentinelSpanVisitor = () => ({
  visit: (node: EventNode): EventNode[] => (isSentinelSpan(node) ? [] : [node]),
});

export const removeStepSpanNameVisitor = (name: string) => {
  return {
    visit: (node: EventNode): EventNode[] => {
      if (
        (node.event.event === "step" || node.event.event === "span_begin") &&
        node.event.name === name
      ) {
        return [];
      }
      return [node];
    },
  };
};

/**
 * The filter the outline tree applies, reused by turn numbering so both count
 * turns the same way - drops non-turn-content events. The outline also collapses
 * scorer children via noScorerChildren(), which turn numbering deliberately keeps
 * (so model-graded scorer calls count as turns).
 */
export const outlineFilterVisitors = () => [
  removeNodeVisitor("logger"),
  removeNodeVisitor("info"),
  removeNodeVisitor("state"),
  removeNodeVisitor("store"),
  removeNodeVisitor("approval"),
  removeNodeVisitor("review"),
  removeNodeVisitor("sentinel"),
  removeSentinelSpanVisitor(),
  removeNodeVisitor("input"),
  removeNodeVisitor("sandbox"),
  removeStepSpanNameVisitor(kSandboxSignalName),
];

export const noScorerChildren = () => {
  let inScorers = false;
  let inScorer = false;
  let currentDepth = -1;
  return {
    visit: (node: EventNode): EventNode[] => {
      // Note once we're in the scorers span
      if (
        node.event.event === "span_begin" &&
        node.event.type === TYPE_SCORERS
      ) {
        inScorers = true;
        return [node];
      }

      if (
        (node.event.event === "step" || node.event.event === "span_begin") &&
        node.event.type === TYPE_SCORER
      ) {
        inScorer = true;
        currentDepth = node.depth;
        return [node];
      }

      if (inScorers && inScorer && node.depth === currentDepth + 1) {
        return [];
      }
      return [node];
    },
  };
};

export const makeTurns = (eventNodes: EventNode[]): EventNode[] => {
  const results: EventNode[] = [];
  let modelNode: EventNode | null = null;
  const toolNodes: EventNode[] = [];
  let turnCount = 1;

  const makeTurn = (force?: boolean) => {
    if (modelNode !== null && (force || toolNodes.length > 0)) {
      // Create a new "turn" node based on the model event
      const turnNode = new EventNode(
        modelNode.id,
        {
          id: modelNode.id,
          event: "span_begin",
          type: kTurnType,
          name: `turn ${turnCount++}`,
          pending: false,
          working_start: modelNode.event.working_start,
          timestamp: modelNode.event.timestamp,
          parent_id: null,
          span_id: modelNode.event.span_id,
          uuid: null,
          metadata: null,
        },
        modelNode.depth
      );

      // Add the original model event and tool events as children
      turnNode.children = [modelNode, ...toolNodes];
      results.push(turnNode);
    }
    modelNode = null;
    toolNodes.length = 0;
  };

  // Events that can appear between model and tool events without
  // breaking the turn (e.g. logging, informational events).
  const kTurnPassthroughEvents = new Set(["logger", "info"]);

  for (const node of eventNodes) {
    if (node.event.event === "model") {
      // Every model event starts a new turn. Flush the pending turn first
      // (force=true so model-only turns without tools still emit).
      makeTurn(true);
      modelNode = node;
    } else if (node.event.event === "tool") {
      toolNodes.push(node);
    } else if (
      modelNode !== null &&
      kTurnPassthroughEvents.has(node.event.event)
    ) {
      // Absorb logger/info events into the current turn
      toolNodes.push(node);
    } else {
      makeTurn(true);
      results.push(node);
    }
  }
  makeTurn(true);

  return results;
};

export const collapseTurns = (eventNodes: EventNode[]): EventNode[] => {
  const results: EventNode[] = [];
  const collecting: EventNode[] = [];
  const collect = () => {
    if (collecting.length > 0) {
      const numberOfTurns = collecting.length;
      const firstTurn = collecting[0];
      if (!firstTurn) {
        return;
      }
      const turnNode = new EventNode(
        firstTurn.id,
        {
          ...eventNodeOf(firstTurn, "span_begin").event,
          name: `${numberOfTurns} ${numberOfTurns === 1 ? "turn" : "turns"}`,
          type: kTurnsType,
        },
        firstTurn.depth
      );
      results.push(turnNode);
      collecting.length = 0;
    }
  };

  for (const node of eventNodes) {
    if (node.event.event === "span_begin" && node.event.type === kTurnType) {
      // Check depth to ensure we are collecting turns at the same level
      if (collecting.length > 0 && collecting[0]?.depth !== node.depth) {
        collect();
      }

      collecting.push(node);
    } else {
      collect();
      results.push(node);
    }
  }
  // Handle any remaining collected turns
  collect();
  return results;
};

export const collapseScoring = (eventNodes: EventNode[]): EventNode[] => {
  const results: EventNode[] = [];
  const collecting: EventNode[] = [];
  const collect = () => {
    if (collecting.length > 0) {
      const firstScore = collecting[0];
      if (!firstScore) {
        return;
      }
      const turnNode = new EventNode(
        firstScore.id,
        {
          ...eventNodeOf(firstScore, "score").event,
          name: "scoring",
          type: kCollapsedScoring,
        },
        firstScore.depth
      );
      results.push(turnNode);
      collecting.length = 0;
    }
  };

  for (const node of eventNodes) {
    if (node.event.event === "score") {
      collecting.push(node);
    } else {
      collect();
      results.push(node);
    }
  }

  // Handle any remaining collected turns
  collect();
  return results;
};

export type TurnInfo = { turnNumber: number; totalTurns: number };

/**
 * Computes a map from event node IDs to turn info (turn number and total turns).
 * Turn numbers come from the outline-filtered nodes (after makeTurns), so numbering
 * matches the sidebar. Non-turn events inherit the previous turn number.
 *
 * Also returns the inversion, `anchorIdByTurn`: turn number → the turn's model
 * event id, for every numbered turn — including turns whose anchor is hidden
 * by the live collapse state (the keys of `turnMap` cover only visible nodes).
 * Turns without a model child (possible only for backend-emitted turn spans;
 * makeTurns always inserts the model node) have no entry.
 *
 * @param outlineFilteredNodes - The filtered node list used for the outline sidebar
 * @param flattenedNodes - The full flattened node list (all visible transcript events)
 */
export const computeTurnMap = (
  outlineFilteredNodes: EventNode[],
  flattenedNodes: EventNode[]
): { turnMap: Map<string, TurnInfo>; anchorIdByTurn: Map<number, string> } => {
  const turns = makeTurns(outlineFilteredNodes);
  const map = new Map<string, TurnInfo>();

  const turnNodes = turns.filter(
    (n) => n.event.event === "span_begin" && n.event.type === kTurnType
  );
  const totalTurns = turnNodes.length;

  // Map model event IDs to their turn numbers
  let turnNumber = 0;
  const modelEventTurnNumbers = new Map<string, number>();
  const anchorIdByTurn = new Map<number, string>();
  for (const node of turnNodes) {
    turnNumber++;
    const modelChild = node.children.find((c) => c.event.event === "model");
    if (modelChild) {
      modelEventTurnNumbers.set(modelChild.id, turnNumber);
      anchorIdByTurn.set(turnNumber, modelChild.id);
    }
  }

  // Iterate through flattened nodes and assign turn numbers.
  // Non-model events inherit from the most recent model event.
  let currentTurn = 0;
  for (const node of flattenedNodes) {
    const modelTurn = modelEventTurnNumbers.get(node.id);
    if (modelTurn !== undefined) {
      currentTurn = modelTurn;
      map.set(node.id, { turnNumber: currentTurn, totalTurns });
    } else if (currentTurn > 0) {
      map.set(node.id, { turnNumber: currentTurn, totalTurns });
    }
  }

  return { turnMap: map, anchorIdByTurn };
};
